
  import { Component, Prop, Watch } from 'vue-property-decorator'
  import { BaseChart } from '@/components/dashboards/baseChart'
  import { deepCopy } from '@/utils/general'
  import { forwarderColors } from '@/utils/forwarderColors'

@Component({})
  export default class LeadByExecutive extends BaseChart {
  @Prop() leads: Record<string, any>
  @Prop() executives
  executivesAttribute = []
  forwardedSeries: { name: string; group: string; data: any[]; color: string }[] = []
  forwarderColors = forwarderColors

  // Methods
  getCountByExecutive (executive) {
    if (!this.leads?.records) return 0
    if (executive !== 'SIN EJECUTIVO') {
      return this.leads.records.filter(lead => {
        return `${lead.executive?.person?.first_name} ${lead.executive?.person?.surname}` === executive
      }).length
    } else {
      return this.leads.records.filter(lead => !lead.executive).length
    }
  }

  async getExecutives () {
    const executives = await this.executives?.executivesForReports
    if (!executives) return
    this.executivesAttribute = executives.map(executive =>
      executive.first_name + ' ' + executive.surname
    )
  }

  getCountByPipeline (pipeline) {
    if (!this.leads?.records) return 0
    return this.leads?.records.filter(lead => {
      return lead.pipeline.name === pipeline.name
    }).length
  }

  async getData () {
    if (!this.leads) return
    const leads = this.leads.records
    if (!leads) return
    const executives = [...new Set(leads.map(lead => {
      if (!lead.executive) return
      return `${lead.executive?.person?.first_name} ${lead.executive?.person?.surname}`
    }))].filter(executive => executive !== undefined).sort()
    if (leads.filter(lead => !lead.executive).length > 0) {
      executives.push('SIN EJECUTIVO')
    }

    await this.getExecutives()

    const pipelines = this.pipelines

    const dataByPipeline = pipelines.map(pipeline => {
      const data = []

      executives.forEach(executive => {
        let filteredByExecutive = []
        if (executive !== 'SIN EJECUTIVO') {
          filteredByExecutive = leads.filter(lead => {
            if (!lead.executive) return
            const executiveName = `${lead.executive?.person?.first_name} ${lead.executive?.person?.surname}`
            return executiveName === executive && lead.pipeline.name === pipeline.name
          })
        } else {
          filteredByExecutive = leads.filter(lead => !lead.executive && lead.pipeline.name === pipeline.name)
        }

        data.push(filteredByExecutive.length)
      })
      return {
        name: pipeline.description,
        group: 'leads',
        data,
        color: pipeline.color.split(' ')[0],
      }
    })

    const dataByForwarder = {
      name: 'Derivado',
      group: 'forwarded',
      data: [],
      color: '#BEAEED',
    }

    const dataByProspected = {
      name: 'Prospectado',
      group: 'forwarded',
      data: [],
      color: '#EFDE72',
    }

    executives.forEach(executive => {
      let filteredByExecutive = []
      if (executive !== 'SIN EJECUTIVO') {
        filteredByExecutive = leads.filter(lead => {
          if (!lead.executive) return
          const executiveName = `${lead.executive?.person?.first_name} ${lead.executive?.person?.surname}`
          return executiveName === executive
        })
      } else {
        filteredByExecutive = leads.filter(lead => !lead.executive)
      }

      const prospected = filteredByExecutive.filter(lead => {
        return lead.created_by?.['x-hasura-user-email'] === lead.executive?.person?.user?.email &&
          !lead.id_forwarder
      })
      const genioForwarded = filteredByExecutive.filter(lead => {
        return lead.created_by?.['x-hasura-user'] === 'system' &&
          !lead.id_forwarder
      })
      const forwarded = filteredByExecutive.filter(lead => lead.id_forwarder)

      dataByForwarder.data.push(genioForwarded.length + forwarded.length)
      dataByProspected.data.push(prospected.length)
    })

    this.series = dataByPipeline.filter(pipeline => pipeline.data.some(data => data !== 0))
    this.forwardedSeries = [dataByForwarder, dataByProspected]

    let maxSum = 0
    this.series
      // .slice(this.series.length - 2, this.series.length - 1)
      .map(serie => serie.data)
      .map(data => deepCopy(data).sort((a, b) => b - a))
      .forEach(data => maxSum += data[0])

    if (maxSum < 4) maxSum += 2
    if (maxSum < 8) maxSum += 4
    else if (maxSum < 16) maxSum += 8
    else maxSum += 16

    this.chartOptions = {
      chart: {
        height: 400,
        stacked: true,
      },
      legend: {
        show: false,
      },
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            total: {
              enabled: true,
              formatter: val => `${val} / ${Math.round(((val / this.leadCount) * 100))}%`,
              offsetX: 16,
              style: {
                fontSize: '13px',
                fontWeight: 900,
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, opt) => {
          return opt.w.globals.series[opt.seriesIndex][opt.dataPointIndex]
        },
      },
      tooltip: {
        y: {
          formatter (val) {
            return val
          },
        },
      },
      fill: {
        opacity: 1,
      },
      xaxis: {
        categories: executives.map(executive => `${executive.split(' ')[0]} ${executive.split(' ')[1].slice(0, 1)}.: ${this.getCountByExecutive(executive)}${this.executivesAttribute.includes(executive) ? `/${Math.round(((this.getCountByExecutive(executive) / this.executiveLeadsCount)) * 100)}%` : ''}`),
        min: 0,
        max: maxSum,
      },
    }
  }

  // Getters
  get pipelines () {
    const pipelines: { name: string, description: string, color: string }[] = []
    if (this.leads?.records) {
      this.leads?.records.forEach(lead => {
        if (!pipelines.map(pipeline => pipeline.name).includes(lead.pipeline.name)) {
          pipelines.push(lead.pipeline)
        }
      })
    }
    return pipelines
  }

  get executiveLeadsCount () {
    if (this.leads?.records) {
      return this.leads?.records
        .filter(lead => {
          const executive = `${lead.executive?.person?.first_name} ${lead.executive?.person?.surname}`
          return this.executivesAttribute.includes(executive)
        })
        .length
    } else return 0
  }

  get forwardedCount () {
    if (!this.leads?.records) return 0
    return this.leads.records.filter(lead => lead.id_forwarder).length +
      this.leads.records.filter(lead => lead.created_by?.['x-hasura-user'] === 'system' &&
        !lead.id_forwarder).length
  }

  get prospectedCount () {
    if (!this.leads?.records) return 0
    return this.leads?.records.filter(lead =>
      lead.created_by?.['x-hasura-user-email'] === lead.executive?.person?.user?.email &&
      !lead.id_forwarder
    ).length
  }

  get leadCount () {
    if (!this.leads?.records) return 0
    return this.leads?.records.length
  }

  // Watchers
  @Watch('leads', { immediate: true, deep: true })
  onDatesChange () {
    this.display = false
    this.getData()
    this.display = true
  }
  }
